import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Trash2, Sparkles, Loader2 } from "lucide-react";
import { useAudioRecorder, formatDuration } from "@/hooks/useAudioRecorder";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface CallAudioRecorderProps {
  // Called when user wants to apply the AI-generated content to the parent form
  onTranscribed: (transcript: string, nextSteps: string) => void;
  // Called whenever the audio state changes so parent knows what to upload
  onAudioChange: (data: { blob: Blob | null; durationSeconds: number; transcribed: boolean }) => void;
  // Disable controls while parent is saving
  disabled?: boolean;
}

export function CallAudioRecorder({ onTranscribed, onAudioChange, disabled }: CallAudioRecorderProps) {
  const { toast } = useToast();
  const { status, durationSeconds, audioBlob, audioUrl, start, stop, reset } = useAudioRecorder();
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [hasTranscribed, setHasTranscribed] = useState(false);

  const handleStop = () => {
    stop();
    // audioBlob will be set on recorder.onstop; notify parent next tick via effect-like pattern below
    // We use a small timeout because setAudioBlob is async inside the hook callback.
    setTimeout(() => {
      // Parent gets the latest blob through the effect below
    }, 0);
  };

  // Notify parent on each state transition
  // (using an inline effect through render — safe because state changes are stable)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useStateChangeNotifier(audioBlob, durationSeconds, hasTranscribed, onAudioChange);

  const handleReset = () => {
    reset();
    setHasTranscribed(false);
    onAudioChange({ blob: null, durationSeconds: 0, transcribed: false });
  };

  const handleTranscribe = async () => {
    if (!audioBlob) return;
    try {
      setIsTranscribing(true);

      // Convert blob to base64
      const arrayBuffer = await audioBlob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      let binary = "";
      const chunkSize = 0x8000;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
      }
      const base64 = btoa(binary);

      const { data, error } = await supabase.functions.invoke('transcribe-call-audio', {
        body: { audioBase64: base64, mimeType: audioBlob.type || 'audio/webm' },
      });

      if (error) throw error;
      if (!data?.transcript && !data?.nextSteps) {
        throw new Error("Réponse IA vide");
      }

      onTranscribed(data.transcript || "", data.nextSteps || "");
      setHasTranscribed(true);
      onAudioChange({ blob: audioBlob, durationSeconds, transcribed: true });

      toast({
        title: "Transcription terminée",
        description: "Le résumé et les prochaines étapes ont été remplis automatiquement.",
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur inconnue";
      console.error("Transcribe error:", err);
      toast({
        title: "Échec de la transcription",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  return (
    <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Enregistrement audio</span>
        </div>

        {status === "idle" && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={start}
            disabled={disabled || isTranscribing}
          >
            <Mic className="w-4 h-4 mr-2" />
            Enregistrer l'appel
          </Button>
        )}

        {status === "recording" && (
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-2 text-sm font-mono">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-destructive" />
              </span>
              {formatDuration(durationSeconds)}
            </span>
            <Button type="button" variant="destructive" size="sm" onClick={handleStop}>
              <Square className="w-4 h-4 mr-2" />
              Arrêter
            </Button>
          </div>
        )}

        {status === "ready" && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-mono">
              {formatDuration(durationSeconds)}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={disabled || isTranscribing}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleTranscribe}
              disabled={disabled || isTranscribing || hasTranscribed}
            >
              {isTranscribing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Transcription…
                </>
              ) : hasTranscribed ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Transcrit
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Transcrire avec l'IA
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {status === "ready" && audioUrl && (
        <audio controls src={audioUrl} className="w-full h-9" />
      )}

      <p className="text-xs text-muted-foreground">
        L'audio est stocké de manière sécurisée et transcrit via IA. Informez votre interlocuteur si vous l'enregistrez.
      </p>
    </div>
  );
}

// Tiny helper to call onAudioChange whenever the recorder state changes
// without forcing the parent to manage another effect.
import { useEffect, useRef } from "react";
function useStateChangeNotifier(
  blob: Blob | null,
  duration: number,
  transcribed: boolean,
  cb: (data: { blob: Blob | null; durationSeconds: number; transcribed: boolean }) => void
) {
  const lastSentRef = useRef<string>("");
  useEffect(() => {
    const sig = `${blob ? blob.size : 0}-${duration}-${transcribed}`;
    if (sig !== lastSentRef.current) {
      lastSentRef.current = sig;
      cb({ blob, durationSeconds: duration, transcribed });
    }
  }, [blob, duration, transcribed, cb]);
}
